import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { ClickAwayListener, MenuList } from "@mui/material";
import ButtonGroup, { ButtonGroupProps } from "@mui/material/ButtonGroup";
import { Button, MenuItem, OverflowText, PopperInContainer } from "designSystem/index";
import { useLocale } from "designSystem/locale";
import React, { FC, useState } from "react";
import { getDialogZIndex } from "csi/utility/htmlUtility";

export interface SplitButtonItem {
	/** localization resource to use to lookup text; can be simple resource or type resource (i.e. "User.Name") */
	res?: string;

	/** parameters to pass if looking up resource */
	resParams?: Array<number | string>;

	/** text for the button */
	text?: string;
}

export interface SplitButtonProps extends Omit<ButtonGroupProps, "onClick"> {
	/** the buttons to show */
	buttons: SplitButtonItem[];

	/** callback for button item onclick */
	onClick?: (item: SplitButtonItem) => void;
}

const SplitButton: FC<SplitButtonProps> = (props) => {
	const [anchorEl, setAnchorEl] = useState(null);
	const loc = useLocale();

	const { buttons, onClick, ...otherProps } = props;

	// populate the button item names
	buttons.forEach((button) => {
		const { res, resParams, text } = button;
		if (!text && res) {
			button.text = loc.resource(res, resParams);
		}
	});

	const primaryButton = buttons[0];
	const otherButtons = buttons.slice(1);

	/** callback for the arrow button */
	function onClickArrow(event: React.MouseEvent<HTMLButtonElement>): void {
		setAnchorEl(anchorEl ? null : event.currentTarget);
	};

	/** callback for the primary button click */
	function handleClickPrimaryButton(): void {
		handleMenuItemClick(primaryButton);
	};

	/** handler for button click */
	function handleMenuItemClick(button: SplitButtonItem): void {
		onClick?.(button);
		handleClose();
	};

	/** close the action container */
	function handleClose(): void {
		setAnchorEl(null);
	}

	return (
		<>
			<ButtonGroup variant="contained" {...otherProps}>
				<Button
					design="secondary"
					onClick={handleClickPrimaryButton}
					res={primaryButton.res || primaryButton.text}
					resParams={primaryButton.resParams}
				/>
				<Button
					design="secondary"
					onClick={onClickArrow}
					size="small"
				>
					<ArrowDropDownIcon />
				</Button>
			</ButtonGroup>
			<PopperInContainer
				anchorEl={anchorEl}
				paperProps={{
					sx: {
						display: "flex",
						flexDirection: "column",
						maxHeight: "300px",
						overflow: "auto",
					},
				}}
				sx={{ zIndex: getDialogZIndex() }}
			>
				<ClickAwayListener onClickAway={handleClose}>
					<MenuList autoFocusItem>
						{otherButtons.map((button, index) => (
							<MenuItem
								key={index}
								onClick={() => handleMenuItemClick(button)}
							>
								<OverflowText text={button.text} />
							</MenuItem>
						))}
					</MenuList>
				</ClickAwayListener>
			</PopperInContainer>
		</>
	);
};

export default SplitButton;